import { GithubLogoIcon, ArrowSquareOutIcon, GearIcon, PlusCircleIcon } from '@phosphor-icons/react';

import { Button, LinkButton } from '@/ui/button/Button';
import { ProviderCard } from '@/features/settings/components/ProviderCard';
import { StatusPill } from '@/features/settings/components/StatusPill';
import { useGitHubStatus } from '@/api-models/integrations';

interface GitHubConnectionCardProps {
  /** Where to redirect after the OAuth flow. */
  redirectTo: string;
}

export function GitHubConnectionCard({ redirectTo }: GitHubConnectionCardProps) {
  const { status, isLoading } = useGitHubStatus();

  const handleConnect = () => {
    window.location.href = `/api/v1/auth/github?redirect=${encodeURIComponent(redirectTo)}`;
  };

  if (isLoading) {
    return (
      <ProviderCard
        icon={<GithubLogoIcon size={22} weight="duotone" className="text-fg-secondary" />}
        name="GitHub"
        description="Checking connection…"
      />
    );
  }

  const isValid = status?.connected && status.tokenValid === true;
  const isInvalid = status?.connected && status.tokenValid === false;
  const notConnected = !status?.connected;

  const description = isValid
    ? status?.githubUsername
      ? `Signed in as @${status.githubUsername} · repository access granted`
      : 'Signed in · repository access granted'
    : isInvalid
      ? 'Your GitHub access expired or was revoked. Reconnect to keep using your repositories.'
      : 'Connect GitHub to start workspaces from any branch of your repositories.';

  const badge = isValid ? (
    <StatusPill tone="success">Connected</StatusPill>
  ) : isInvalid ? (
    <StatusPill tone="warning">Reconnect needed</StatusPill>
  ) : undefined;

  const showRepoAccess = status?.connected && (status.installationUrl || status.installNewUrl);

  return (
    <ProviderCard
      icon={<GithubLogoIcon size={22} weight="duotone" className="text-fg" />}
      name="GitHub"
      badge={badge}
      description={description}
      actions={
        <Button
          type="button"
          variant={isInvalid || notConnected ? 'primary' : 'secondary'}
          onClick={handleConnect}
          iconLeft={<ArrowSquareOutIcon size={14} />}
          size={8}
        >
          {notConnected ? 'Connect' : 'Reconnect'}
        </Button>
      }
    >
      {showRepoAccess && (
        <div className="flex flex-col gap-2">
          <span className="text-xs text-fg-tertiary">
            Choose which GitHub organizations and repositories zooid is allowed to access.
          </span>
          <div className="flex items-center gap-2">
            {status?.installationUrl && (
              <LinkButton
                to={status.installationUrl}
                isExternal
                target="_blank"
                variant="secondary"
                size={8}
                iconLeft={<GearIcon size={13} />}
                iconRight={<ArrowSquareOutIcon size={11} className="text-fg-muted" />}
              >
                Manage permissions
              </LinkButton>
            )}
            {status?.installNewUrl && (
              <LinkButton
                to={status.installNewUrl}
                isExternal
                target="_blank"
                variant="secondary"
                size={8}
                iconLeft={<PlusCircleIcon size={13} />}
                iconRight={<ArrowSquareOutIcon size={11} className="text-fg-muted" />}
              >
                Add organization
              </LinkButton>
            )}
          </div>
        </div>
      )}
    </ProviderCard>
  );
}
